'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import ActionButton from './ActionButton';
import Leaderboard from './Leaderboard';
import PrizePool from './PrizePool';
import BannerBox from '../BannerBox';
import GameBannerContainer from '../GameBannerContainer';
import { useTournamentUpdatedSubscription } from '../graphql/TournamentUpdatedSubscription.generated';
import TournamentInfo from '../TournamentInfo';
import ToastContextProvider from '@/client/components/Toast/Context/ToastContextProvider';
import TournamentTimer from '@/client/components/Tournaments/PrizeCountdown/TournamentTimer';
import { TournamentFragment } from '@/shared/graphql/TournamentFragment.generated';
import { getTournamentQuery } from '@/shared/graphql/TournamentQuery.generated';

type Props = {
  tournaments?: Maybe<TournamentFragment>[];
  translations: Record<string, string>;
  isMobile?: boolean;
  isLoggedIn: boolean;
  walletCurrency: string;
  site: Site;
};

const BannerView: FC<Props> = ({
  tournaments,
  translations,
  isMobile,
  isLoggedIn,
  walletCurrency,
  site,
}) => {
  const searchParams = useSearchParams();
  const [tournamentData, setTournamentData] = useState<
    TournamentFragment | undefined
  >();

  useEffect(() => {
    const handle = searchParams?.get('tournament');
    const preferredTournament = tournaments?.find(
      (tournament) => tournament?.shortUrl === handle,
    );
    setTournamentData(preferredTournament || tournaments?.[0]);
  }, [searchParams, tournaments]);

  useTournamentUpdatedSubscription({
    variables: {
      input: {
        tournamentId: tournamentData?.id,
      },
    },
    onData: (data) => {
      const updatedTournament =
        data.casinoTournamentsPublicTournamentUpdated?.tournament;
      setTournamentData(updatedTournament);
    },
  });

  const updateTournament = async () => {
    if (!tournamentData) {
      return;
    }
    const { data } = await getTournamentQuery({
      tournamentId: tournamentData.id,
      site,
    });
    setTournamentData(data?.casinoTournamentsPublic?.tournament);
  };

  if (!tournamentData) {
    return null;
  }

  const isBitcasino = site === 'bitcasino';

  return (
    <ToastContextProvider>
      <GameBannerContainer>
        <TournamentInfo
          tournament={tournamentData}
          translations={translations}
          isMobile={isMobile}
          isBitcasino={isBitcasino}
        />
        <BannerBox className="justify-center gap-1">
          <TournamentTimer
            tournament={tournamentData}
            isGameBanner
            translations={translations}
          />
        </BannerBox>
        <BannerBox className="justify-center gap-1">
          <PrizePool
            translations={translations}
            prize={tournamentData.prizeText}
          />
        </BannerBox>
        <BannerBox>
          <Leaderboard
            isBitcasino={isBitcasino}
            isLoggedIn={isLoggedIn}
            tournament={tournamentData}
            translations={translations}
            walletCurrency={walletCurrency}
            updateTournament={updateTournament}
          />
        </BannerBox>
        <ActionButton tournament={tournamentData} translations={translations} />
      </GameBannerContainer>
    </ToastContextProvider>
  );
};

export default BannerView;
